/*
 * Copyright (c) Kalaora 2022.
 * Author: fharold
 *
 * hilo-oauth-manager
 * HomePage.tsx
 */

import {PolicyListener} from "../../services/PolicyService";
import Policy from "../../tools/provider/Policy";
import HiloReactComponent from "../../tools/view/layout/HiloReactComponent";
import React from "react";
import PageTitle, {TitleTab} from "../../tools/view/layout/PageTitle";
import Catalog from "../../models/Catalog";
import CatalogAdapter, {CatalogAdapterListener} from "../adapter/CatalogAdapter";
import CatalogForm from "../../models/form/catalog/CatalogForm";
import UIToast from "../../tools/view/toast/UIToast";
import FormModal from "../../tools/view/layout/FormModal";
import ReactDOM from "react-dom";
import NavigationService from "../../tools/service/NavigationService";
import List from "../../tools/list/List";
import Spinner from "../../tools/view/element/Spinner";
import PageComponent from "../../tools/view/layout/PageComponent";
import CatalogService from "../../services/CatalogService";
import CatalogDataProvider from "../../provider/CatalogDataProvider";

interface State {
    policy: Policy;
    pageIndex: number;
    catalogDataProvider: CatalogDataProvider;
    catalogAdapter?: CatalogAdapter;
}

export default class CatalogsPage extends HiloReactComponent<{}, State> implements PolicyListener, CatalogAdapterListener {

    private readonly _navigationService: NavigationService;
    private readonly _catalogService: CatalogService;
    private _modalContainer?: Element;
    private _catalogForm: CatalogForm;

    /**
     * constructor
     * @param props
     * @param state
     */
    constructor(props: {}, state: State) {
        super(props, state);

        this._navigationService = this._serviceRepository.resolve(NavigationService.id) as NavigationService;
        this._catalogService = this._serviceRepository.resolve(CatalogService.id) as CatalogService;

        const catalogDataProvider = new CatalogDataProvider(this._catalogService);

        this.state = {
            policy: this._policyService.policy,
            pageIndex: 0,
            catalogDataProvider: catalogDataProvider,
            catalogAdapter: new CatalogAdapter(this.t, catalogDataProvider)
        };

        this._catalogForm = new CatalogForm()
    }

    /**
     * rowSelected
     * @param obj
     */
    public rowSelected(obj: Catalog): void {
        this._navigationService.load(`/catalog/${obj.id}`)
    }

    /**
     * componentDidMount
     */
    public componentDidMount(): void {
        super.componentDidMount();
        this._catalogService.addListener(this);
        this._modalContainer = document.querySelector('#modal-container') || undefined;
        this._policyService.addListener(this);

        if (this.state.catalogAdapter) {
            this.state.catalogAdapter.listener = this;
        }

        // this.state.catalogDataProvider.subscribe(this);
    }

    /**
     * componentWillUnmount
     */
    public componentWillUnmount(): void {
        this._policyService.removeListener(this);
        this._catalogService.removeListener(this);

        // this.state.catalogDataProvider.unsubscribe(this);

        super.componentWillUnmount();
    }

    /**
     * policyUpdated
     * @param policy
     */
    public policyUpdated = (policy: Policy) => {
        this.setState({
            policy: policy
        })
    };

    /**
     * _displayAddUserModal
     * @private
     */
    private _displayAddUserModal = async () => {
        if (!this._modalContainer || !this._catalogForm) {
            return;
        }

        this._catalogForm._clearForm();

        const modal = <FormModal
            emptyForm={this._catalogForm}
            title={t => t.newCatalog}
            onSaveClicked={this._saveCatalog}
        />;

        ReactDOM.render(modal, this._modalContainer);
    }

    /**
     * _catalogList
     * @returns {any}
     * @private
     */
    private _catalogList = (props: { provider?: CatalogDataProvider, adapter?: CatalogAdapter }) => {
        const {provider, adapter} = props;

        if (!adapter || !provider) return <Spinner/>;

        return <List adapter={adapter}
                     dataProvider={provider}
                     onAddClick={this._displayAddUserModal}/>
    }

    /**
     * _saveCatalog
     * @param dto
     * @private
     */
    private _saveCatalog = async (dto: Map<string, any> | FormData) => {
        try {
            await this._catalogService.createCatalog(dto);
            if (this._catalogForm) this._catalogForm.statusCode = 201;
            UIToast.default.show(this.t.catalogAdded)

            this.state.catalogDataProvider.load()

        } catch (e) {
            if (this._catalogForm) {
                this._catalogForm.statusCode = e.status
            }
            console.error(e);
        }
    };

    /**
     * render
     */
    public render(): any {
        const {catalogDataProvider, catalogAdapter} = this.state;

        const titleTabs: TitleTab[] = [
            {title: t => t.list, content: <this._catalogList provider={catalogDataProvider} adapter={catalogAdapter}/>}
        ];

        return <PageComponent>
            <PageTitle title={t => t.catalogs} tabs={titleTabs}
                       onTabSelected={index => this.setState({pageIndex: index})}/>
            {
                titleTabs[this.state.pageIndex].content
            }
        </PageComponent>
    }
}